import { ButtonLayout } from '@empiriecom/module-components/Button/types';
import { CookieCategory } from '@empiriecom/module-components/hooks/useCookieConsent';
import { Locale } from '@empiriecom/module-components/IntlProvider';
import { PriceProps } from '@empiriecom/module-components/Price';
import { CompanyId } from '@empiriecom/module-components/utils/interfaces';

const { FRAGMENT_HOST = 'emma.empirie.cloud' } = process.env;

export interface RatingFormField {
  name:
    | 'headline'
    | 'review'
    | 'name'
    | 'city'
    | 'advantagesHint'
    | 'advantages'
    | 'disadvantages'
    | 'terms';
  type: 'text' | 'textarea' | 'hint' | 'tag' | 'checkbox';
}

export interface ExternalService {
  apiKey?: string;
  apiUrl?: string;
  asyncScript?: boolean;
  consentGroup?: CookieCategory;
  containerName?: string;
  customerId?: string;
  debugEnabled?: boolean;
  enabled?: boolean;
  moreInformationLink?: string;
  scriptUrl?: string;
}

export type PriceLayout = PriceProps['layout'];

export interface FragmentConfiguration {
  addToBasketButton: {
    layout: ButtonLayout;
  };
  addToWishlistButton: {
    iconFillColor: 'primary' | 'secondary';
  };
  api: {
    enabled: boolean;
    values: {
      productApiEndpoint: string;
      apiRatingsESI: string;
      apiAddToBasketESI: string;
      breadcrumbApiEndpoint: string;
    };
    serverHost: string;
    clientHost: string;
    clientHostStaging: string;
  };
  articleServices: {
    enabled: boolean;
    articleServicesEndpoint: string;
    serviceIconUrl: string;
    serviceModalUrl: string;
    currency: 'EUR' | 'CHF';
  };
  availabilityMessage: {
    newLayout: boolean;
  };
  company: {
    host: string;
    id: CompanyId;
    name: string;
    seoTitleName: string;
  };
  companyId: CompanyId;
  customerRatings: {
    enabled: boolean;
    iconType: 'SHOE' | 'STAR';
    iconColor: string;
    values: {
      summary: string;
      filter: string;
      helpful: string;
      new: string;
      ratingPaginationURL: string;
      pagingCount: string;
      termsESI: string;
      submitRatingURL: string;
    };
    formConfig: {
      starValues: number[];
      fields: RatingFormField[];
    };
  };
  dynamicYieldEnabled: boolean;
  esi: {
    topBanner: {
      enabled: boolean;
      url: string;
    };
  };
  expressService: {
    modalContent: string;
    apiEndpointUrl: string;
  };
  externalServices: {
    cliplister?: ExternalService;
    grover?: ExternalService;
    loadbee?: ExternalService;
  };
  galleryProductImageFormat: {
    desktop: string;
    mobile: string;
  };
  galleryZoomImage: {
    mobile: {
      width: number;
      height: number;
      formatName: string;
    };
    desktop: {
      width: number;
      height: number;
      formatName: string;
    };
  };
  installmentCalculator: {
    apiInstallmentCalculator: string;
    borderColor: string;
    cardIdentifier: string;
    hasSmallPrize: boolean;
    cardLayout: boolean;
    useExtendedLabel: boolean;
    useLargeLinkText: boolean;
  };
  meta: {
    enabled: boolean;
    values: {
      metaApiEndpoint: string;
    };
  };
  page: {
    enabled: boolean;
    values: {
      footerPath: string;
      googleOptimizeContainerId: string;
      googleOptimizeEnabled: boolean;
      googleOptimizeExperimentId: string;
      locales: {
        locale: Locale;
        prefix: string;
      }[];
      defaultLocale: Locale;
    };
  };
  price: {
    layout: PriceLayout;
  };
  productAttributes: string[];
  productSelection: {
    borderColor: 'PRIMARY' | 'DEFAULT';
  };
  recommendationAPI: string;
  seo: {
    enabled: boolean;
    values: {
      seoApiEndpoint: string;
    };
  };
  specialHint: {
    enabled: boolean;
    esiUrl?: string;
    hintLevel: 'info' | 'warning' | 'error' | 'success';
  };
  taxHint: {
    useESI: boolean;
    modalContent: {
      s: {
        link: string;
        useLink: boolean;
      };
      l: {
        link: string;
        useLink: boolean;
      };
      default: {
        link: string;
        useLink: boolean;
      };
    };
  };
  tracking: {
    values: {
      brOutcomeUrl: string;
      googleSiteVerification: string;
      msvalidate01: string;
      soastaId: string;
      siteName: string;
    };
    schemaOrgOrganisation: {
      name: string;
      url: string;
      logo: string;
      sameAs: string[];
      telephone: string;
      productSupported: string;
      email: string;
      hoursAvailable: string;
      areaServed: string;
    };
  };
  voucherPrice: {
    enabled: boolean;
  };
}

const config: FragmentConfiguration = {
  addToBasketButton: {
    layout: 'primary',
  },
  addToWishlistButton: {
    iconFillColor: 'primary',
  },
  api: {
    clientHost: 'https://www.baur.de',
    clientHostStaging: 'https://baurde.empirie.dev',
    enabled: true,
    serverHost: 'http://<service>.<namespace>.svc.cluster.local:<port>',
    values: {
      apiAddToBasketESI: '/defaultConfig/ATB',
      apiRatingsESI: `//router.${FRAGMENT_HOST}/p/<seoid><productid>?emmaMode=true`,
      productApiEndpoint: '/api/p/<locale>/<productid>',
      breadcrumbApiEndpoint: '/api/b',
    },
  },
  articleServices: {
    articleServicesEndpoint: '/defaultConfig/ProductServicesAPI',
    currency: 'EUR',
    enabled: true,
    serviceIconUrl: '/defaultConfig/ServiceIcon',
    serviceModalUrl: '/defaultConfig/ServiceModal',
  },
  availabilityMessage: {
    newLayout: false,
  },
  company: {
    host: 'platform.empirie.dev',
    id: 'emma',
    name: 'Emma',
    seoTitleName: 'Emma',
  },
  companyId: 'emma',
  customerRatings: {
    enabled: true,
    formConfig: {
      fields: [
        { name: 'headline', type: 'text' },
        { name: 'review', type: 'textarea' },
        { name: 'name', type: 'text' },
        { name: 'advantagesHint', type: 'hint' },
        { name: 'advantages', type: 'tag' },
        { name: 'disadvantages', type: 'tag' },
        { name: 'terms', type: 'checkbox' },
      ],
      starValues: [1, 2, 3, 4, 5],
    },
    iconColor: '#fcd500',
    iconType: 'SHOE',
    values: {
      filter: '/p/<productid>/rating/f-<tagid>',
      helpful: '/defaultConfig/RatingHelpful',
      new: '/p/<productid>/rating/new',
      pagingCount: '10',
      ratingPaginationURL: '/defaultConfig/RatingPagination',
      submitRatingURL: '/defaultConfig/RatingSubmit',
      summary: '/defaultConfig/RatingSummary',
      termsESI: '/defaultConfig/RatingTerms',
    },
  },
  dynamicYieldEnabled: true,
  esi: {
    topBanner: {
      enabled: true,
      url: `http://router.${FRAGMENT_HOST}/{{locale}}/landingpages/fragment-minibanner/?emmaMode=true`,
    },
  },
  expressService: {
    apiEndpointUrl: '/api/deliverydate',
    modalContent: '/defaultConfig/ExpressModal',
  },
  externalServices: {
    cliplister: {
      apiUrl: '/api/inform/cliplister',
      consentGroup: 'C0004',
      enabled: true,
      scriptUrl:
        'https://mycliplister.com/merge?cliplister=1.6&clviewer=1.6&videostage=1.3&innercontrols=1.3&clickablevideo=1.4&playbutton=1.1&previewimage=1.2&bufferingspinner=1.5',
    },
    grover: {
      consentGroup: 'C0004',
      enabled: false,
      moreInformationLink: '/grover',
    },
    loadbee: {
      asyncScript: true,
      consentGroup: 'C0004',
      containerName: 'loadbeeTabContent',
      debugEnabled: false,
      enabled: true,
      scriptUrl: 'https://cdn.loadbee.com/js/loadbee_integration.js',
    },
  },
  galleryProductImageFormat: {
    desktop: 'format_dv_desk_132',
    mobile: 'format_dv_mob_075',
  },
  galleryZoomImage: {
    desktop: {
      formatName: 'format_dv_zoom_desk_35',
      height: 3500,
      width: 3500,
    },
    mobile: {
      formatName: 'format_dv_zoom_mob_10',
      height: 1000,
      width: 1000,
    },
  },
  installmentCalculator: {
    apiInstallmentCalculator: '/api/inform/installment',
    borderColor: '#c5c5c5',
    cardIdentifier: 'card-installment-calculator',
    cardLayout: false,
    hasSmallPrize: true,
    useExtendedLabel: true,
    useLargeLinkText: false,
  },
  meta: {
    enabled: true,
    values: {
      metaApiEndpoint: '/meta?SKU=<productid>',
    },
  },
  page: {
    enabled: true,
    values: {
      defaultLocale: 'de-DE',
      footerPath: '/fragment/footer',
      googleOptimizeContainerId: '',
      googleOptimizeEnabled: false,
      googleOptimizeExperimentId: '',
      locales: [
        {
          locale: 'de-DE',
          prefix: '',
        },
      ],
    },
  },
  price: {
    layout: 'detailViewSecondary',
  },
  productAttributes: ['legal', 'flags', 'downloads'],
  productSelection: {
    borderColor: 'DEFAULT',
  },
  recommendationAPI: '/api/inform/graphql/',
  seo: {
    enabled: true,
    values: {
      seoApiEndpoint: '/api/homer/content/<productid>',
    },
  },
  specialHint: {
    enabled: false,
    hintLevel: 'info',
  },
  taxHint: {
    modalContent: {
      default: { link: '/defaultConfig/TaxHintEsiDefault', useLink: true },
      l: { link: '/defaultConfig/TaxHintEsiL', useLink: true },
      s: { link: '/defaultConfig/TaxHintEsiS', useLink: true },
    },
    useESI: true,
  },
  tracking: {
    schemaOrgOrganisation: {
      areaServed: 'DE',
      email: 'service@empirie.dev',
      hoursAvailable: 'täglich von 06:00 - 23:00 Uhr',
      logo: '/logo.svg',
      name: 'EMMA Shop',
      productSupported: '',
      sameAs: [],
      telephone: '',
      url: 'https://www.empirie.dev',
    },
    values: {
      brOutcomeUrl: '/defaultOutcomeUrl',
      googleSiteVerification: '',
      msvalidate01: '',
      siteName: '@defaultSitename',
      soastaId: '',
    },
  },
  voucherPrice: {
    enabled: true,
  },
};

export default config;
